import { createContext } from "react";
import openSocket from "socket.io-client";
import { getBackendBaseUrl } from "../../utils/backendUrl";
import jwt from "jsonwebtoken";
import {
  getStoredAuthToken,
} from "../../utils/authTokenStorage";

class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = [];
    this.readyCallbacks = [];
    this.joins = [];
  }

  on(event, callback) {
    if (event === "ready" || event === "connect") {
      const unsubscribe = this.socketManager.onReady(callback);
      this.readyCallbacks.push({ event, callback, unsubscribe });
      return unsubscribe;
    }
    this.callbacks.push({ event, callback });
    return this.rawSocket.on(event, callback);
  }

  off(event, callback) {
    if (event === "ready" || event === "connect") {
      this.readyCallbacks = this.readyCallbacks.filter(subscription => {
        if (subscription.event !== event || (callback && subscription.callback !== callback)) return true;
        subscription.unsubscribe();
        return false;
      });
      return;
    }
    const i = this.callbacks.findIndex((c) => c.event === event && c.callback === callback);
    if (i !== -1) this.callbacks.splice(i, 1);
    return this.rawSocket.off(event, callback);
  }

  emit(event, ...params) {
    if (event.startsWith("join")) {
      const joinEvent = event.substring(4);
      this.joins.push({ event: joinEvent, params });
      this.socketManager.registerJoin(joinEvent, params);
    }
    return this.rawSocket.emit(event, ...params);
  }

  disconnect() {
    for (const subscription of this.readyCallbacks) subscription.unsubscribe();
    this.readyCallbacks = [];
    for (const j of this.joins) {
      this.rawSocket.emit(`leave${j.event}`, ...j.params);
      this.socketManager.unregisterJoin(j.event, j.params);
    }
    this.joins = [];
    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
    }
    this.callbacks = [];
  }
}

class DummySocket {
  on(..._) { }
  off(..._) { }
  emit(..._) { }
  disconnect() { }
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,
  globalJoins: [],
  refreshHandler: null,
  disconnectedAt: null,

  getCurrentAuthPayload: function () {
    const token = getStoredAuthToken();
    if (!token) {
      return null;
    }

    try {
      const decoded = jwt.decode(token);
      if (decoded?.exp && Date.now() >= decoded.exp * 1000) {
        return { token, expired: true };
      }
    } catch (_error) {
      // keep token as-is and let backend validation decide
    }

    return { token, expired: false };
  },

  getCurrentAuthToken: function () {
    return this.getCurrentAuthPayload()?.token || null;
  },

  emitClientMetric: function (event, payload = {}) {
    try {
      const metric = {
        event,
        payload,
        at: new Date().toISOString()
      };
      window.__NEO_SOCKET_METRICS__ = window.__NEO_SOCKET_METRICS__ || [];
      window.__NEO_SOCKET_METRICS__.push(metric);
      if (window.__NEO_SOCKET_METRICS__.length > 200) {
        window.__NEO_SOCKET_METRICS__.splice(0, window.__NEO_SOCKET_METRICS__.length - 200);
      }

      if (window?.Sentry?.captureMessage) {
        window.Sentry.captureMessage(`socket:${event}`, {
          level: event === "connect_error" ? "warning" : "info",
          extra: payload
        });
      }
    } catch (_error) {
      // metric emission is best-effort only
    }
  },

  syncAuthToCurrentSocket: function (token) {
    if (!this.currentSocket) {
      return;
    }

    if (!token) {
      return;
    }

    this.currentSocket.auth = {
      ...(this.currentSocket.auth || {}),
      token
    };

    if (process.env.REACT_APP_SOCKET_LEGACY_QUERY_TOKEN === "true") {
      this.currentSocket.io.opts.query = {
        ...(this.currentSocket.io.opts.query || {}),
        token
      };
    }
  },

  updateAuthToken: function (token) {
    this.syncAuthToCurrentSocket(token || this.getCurrentAuthToken());
  },

  resetConnection: function ({ clearJoins = true } = {}) {
    if (this.currentSocket) {
      try {
        this.currentSocket.removeAllListeners();
        if (
          this.currentSocket.io &&
          typeof this.currentSocket.io.removeAllListeners === "function"
        ) {
          this.currentSocket.io.removeAllListeners();
        }
        this.currentSocket.disconnect();
      } catch (_error) {
        // best-effort cleanup
      }
    }

    this.currentSocket = null;
    this.socketReady = false;
    this.currentCompanyId = -1;
    this.currentUserId = -1;
    if (clearJoins) {
      this.globalJoins = [];
    }
  },

  setRefreshHandler: function (handler) {
    this.refreshHandler = typeof handler === "function" ? handler : null;
  },

  clearRefreshHandler: function (handler) {
    if (!handler || this.refreshHandler === handler) {
      this.refreshHandler = null;
    }
  },

  getRefreshHandler: function () {
    return this.refreshHandler;
  },

  refreshSocketAuthToken: async function ({ force = false, reason = "socket" } = {}) {
    if (typeof this.refreshHandler === "function") {
      return this.refreshHandler({ reason, force });
    }

    const nextToken = getStoredAuthToken();
    if (!nextToken) {
      return { status: "expired" };
    }

    return { status: "restored", token: nextToken };
  },

  attachManagerLifecycleListeners: function () {
    if (!this.currentSocket?.io) {
      return;
    }

    this.currentSocket.io.on("reconnect_attempt", (attempt) => {
      const token = this.getCurrentAuthToken();
      this.syncAuthToCurrentSocket(token);
      this.emitClientMetric("reconnect_attempt", { attempt });
    });

    this.currentSocket.io.on("reconnect", (attempt) => {
      this.emitClientMetric("reconnect", { attempt });
    });

    this.currentSocket.io.on("reconnect_failed", () => {
      this.emitClientMetric("reconnect_failed");
    });
  },

  registerJoin: function (event, params) {
    const exists = this.globalJoins.find(j => j.event === event && JSON.stringify(j.params) === JSON.stringify(params));
    if (!exists) {
      this.globalJoins.push({ event, params });
    }
  },

  unregisterJoin: function (event, params) {
    const i = this.globalJoins.findIndex(j => j.event === event && JSON.stringify(j.params) === JSON.stringify(params));
    if (i !== -1) this.globalJoins.splice(i, 1);
  },

  getSocket: function (companyId) {
    let userId = localStorage.getItem("userId");

    // Allow connection recovery if companyId is missing but we have a socket
    if (!companyId && this.currentSocket) {
      return new ManagedSocket(this);
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    // Convert to string for consistent comparison
    const storedCompanyId = this.currentCompanyId ? String(this.currentCompanyId) : null;
    const incomingCompanyId = companyId ? String(companyId) : null;
    const storedUserId = this.currentUserId ? String(this.currentUserId) : null;
    const incomingUserId = userId ? String(userId) : null;

    if (incomingCompanyId !== storedCompanyId || incomingUserId !== storedUserId) {
      if (this.currentSocket) {
        console.warn("closing old socket - company or user changed", {
          oldCompany: this.currentCompanyId,
          newCompany: companyId,
          oldUser: this.currentUserId,
          newUser: userId
        });
        this.resetConnection();
      }

      const currentAuth = this.getCurrentAuthPayload();
      if (!currentAuth?.token) {
        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;

      const useLegacyQueryToken =
        process.env.REACT_APP_SOCKET_LEGACY_QUERY_TOKEN === "true";

      const socketOptions = {
        transports: ["websocket", "polling"],
        withCredentials: true,
        auth: { token: currentAuth.token },
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity
      };

      if (useLegacyQueryToken) {
        socketOptions.query = { token: currentAuth.token };
      }

      this.currentSocket = openSocket(getBackendBaseUrl(), {
        ...socketOptions
      });
      this.attachManagerLifecycleListeners();

      this.currentSocket.on("connect", () => {
        // Only log if previous state was not ready
        if (!this.socketReady) {
          console.warn("socket connected");
        }
        const disconnectedAt = this.disconnectedAt;
        const disconnectedDurationMs =
          typeof disconnectedAt === "number" ? Date.now() - disconnectedAt : null;
        this.disconnectedAt = null;

        const token = this.getCurrentAuthToken();
        this.syncAuthToCurrentSocket(token);
        this.emitClientMetric("connect", {
          transport: this.currentSocket.io?.engine?.transport?.name,
          disconnectedDurationMs
        });
      });

      this.currentSocket.on("ready", () => {
        if (!this.socketReady) {
          console.info("socket server ready - refreshing joins");
          this.socketReady = true;
          // Re-emit all joined rooms
          for (const j of this.globalJoins) {
            this.currentSocket.emit(`join${j.event}`, ...j.params);
          }
        }
      });

      this.currentSocket.on("connect_error", async (error) => {
        const message = String(error?.message || "").toLowerCase();
        const isAuthError =
          message.includes("auth") ||
          message.includes("session") ||
          message.includes("token") ||
          message.includes("user not found");

        this.emitClientMetric("connect_error", {
          message: error?.message || "unknown",
          isAuthError
        });

        if (!isAuthError) {
          return;
        }

        try {
          const refreshResult = await this.refreshSocketAuthToken({
            reason: "socket-connect-error"
          });
          if (refreshResult?.status === "restored" && refreshResult?.token && this.currentSocket) {
            this.syncAuthToCurrentSocket(refreshResult.token);
            this.currentSocket.connect();
          }
        } catch (_refreshError) {
          // AuthProvider owns final unauthorized handling
        }
      });

      this.currentSocket.on("disconnect", (reason) => {
        console.warn(`socket disconnected: ${reason}`);
        this.socketReady = false;
        this.disconnectedAt = Date.now();
        this.emitClientMetric("disconnect", { reason });
        if (reason === "io server disconnect") {
          // the disconnection was initiated by the server, you need to reconnect manually
          const token = this.getCurrentAuthToken();
          this.syncAuthToCurrentSocket(token);
          this.currentSocket.connect();
        }
        // else the socket will automatically try to reconnect
      });

      this.currentSocket.onAny((event, ...args) => {
        if (process.env.NODE_ENV === "development") {
          //   console.debug("Socket Event: ", event, args);
        }
      });
    }

    if (this.currentSocket) {
      const token = this.getCurrentAuthToken();
      this.syncAuthToCurrentSocket(token);
    }

    return new ManagedSocket(this);
  },

  onReady: function (callbackReady) {
    if (this.socketReady) {
      callbackReady();
      return () => {};
    }

    const socket = this.currentSocket;
    const ready = () => callbackReady();
    socket.once("ready", ready);
    return () => socket.off("ready", ready);
  },

};

const SocketContext = createContext()

export { SocketContext, SocketManager };
